import { CalendarOutlined, ExclamationOutlined, RightCircleOutlined } from '@ant-design/icons';
import { Avatar, Button, Divider, List, Space } from 'antd';
import { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import notificationTrapPlotDiseaseApi from '../../../apis/NotificationTrapPlotDiseaseApi';
import { NotificationTrapPlotDisease } from '../../../models/Entities';
import alertService from '../../../services/AlertService';
import paginationService from '../../../services/TableService';
import styles from './DiseasesComponent.module.scss';

/**
 * Returns the trap plot diseases component.
 * @returns the traps diseases component.
 */
const TrapPlotDiseasesComponent: React.FC<Props> = (props) => {
    const { plotId } = props;

    /*** HOOKS ***/
    const intl = useIntl();
    const [notificationTrapPlotDiseases, setNotificationTrapPlotDiseases] = useState<NotificationTrapPlotDisease[]>([]);
    const [page, setPage] = useState<number>(0);
    const [lastPage, setLastPage] = useState<boolean>(true);
    const [loading, setLoading] = useState<'loading'>();

    // list notifications
    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                const notificationTrapPlotDiseasesPage = await notificationTrapPlotDiseaseApi.list(
                    0,
                    paginationService.pageSize,
                    'notification.auditCreated',
                    false,
                    plotId
                );
                setPage(notificationTrapPlotDiseasesPage.number);
                setLastPage(notificationTrapPlotDiseasesPage.last);
                setNotificationTrapPlotDiseases(notificationTrapPlotDiseasesPage.content);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [intl, plotId]);

    /*** METHODS ***/

    const loadMore = async () => {
        try {
            setLoading('loading');
            const notificationTrapPlotDiseasesPage = await notificationTrapPlotDiseaseApi.list(
                page + 1,
                paginationService.pageSize,
                'notification.auditCreated',
                false,
                plotId
            );
            const updatedNotificationTrapPlotDiseases = [...notificationTrapPlotDiseases, ...notificationTrapPlotDiseasesPage.content];
            setPage(notificationTrapPlotDiseasesPage.number);
            setLastPage(notificationTrapPlotDiseasesPage.last);
            setNotificationTrapPlotDiseases(updatedNotificationTrapPlotDiseases);
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    /*** VISUAL ***/

    return (
        <List
            dataSource={notificationTrapPlotDiseases}
            size="large"
            itemLayout="vertical"
            loading={loading === 'loading'}
            loadMore={
                notificationTrapPlotDiseases && !lastPage ? (
                    <div className={styles.buttons}>
                        <Button type="primary" size="large" onClick={loadMore}>
                            <FormattedMessage id="button.loadMore" />
                        </Button>
                    </div>
                ) : (
                    <></>
                )
            }
            renderItem={(notificationTrapPlotDisease) => (
                <NotificationComponent notificationTrapPlotDisease={notificationTrapPlotDisease} viewLinkVisible={!plotId} />
            )}
        />
    );
};
export default TrapPlotDiseasesComponent;
interface Props {
    plotId: number | undefined;
}

const NotificationComponent: React.FC<NotificationProps> = (props) => {
    const { notificationTrapPlotDisease, viewLinkVisible } = props;
    const { notification } = notificationTrapPlotDisease;

    return (
        <List.Item
            key={notificationTrapPlotDisease.id}
            actions={[
                <Space key="a" className={styles.space} split={<Divider type="vertical" />}>
                    <Space>
                        <CalendarOutlined />
                        <FormattedDate
                            value={notification.audit!.created as any}
                            year="numeric"
                            month="2-digit"
                            day="2-digit"
                            hour="2-digit"
                            minute="2-digit"
                        />
                    </Space>

                    {viewLinkVisible && (
                        <Link to={`/plots/${notificationTrapPlotDisease.trapPlot.plot?.id}`} className={styles.link}>
                            <Button size="small" type="primary" ghost icon={<RightCircleOutlined />}>
                                <FormattedMessage id="notification.view" />
                            </Button>
                        </Link>
                    )}
                </Space>
            ]}
        >
            <List.Item.Meta
                avatar={
                    <Avatar
                        size="small"
                        icon={<ExclamationOutlined />}
                        className={`${styles.avatar} ${notification.level === 'HIGH' ? styles.high : styles.medium}`}
                    />
                }
                title={notification.name}
                description={notification.description}
            />
        </List.Item>
    );
};

interface NotificationProps {
    notificationTrapPlotDisease: NotificationTrapPlotDisease;
    viewLinkVisible?: boolean;
}
